// Miky Motorsport - tire & drivetrain matematika (pure funkce, testovatelne v Node)
// Simplified Pacejka "magic formula" + friction ellipse + slip + engine torque lookup.
// Determinismus neni potreba (ghosti = trajektorie), takze Math.sin/atan jsou OK.

// Pacejka Magic Formula: y = D * sin(C * atan(B*x - E*(B*x - atan(B*x))))
//  x   = slip ratio (podelny) nebo slip angle v rad (pricny)
//  B   = stiffness (sklon u nuly)
//  C   = shape (tvar; ~1.3-1.7)
//  D   = peak sila (= mu * normalova sila N)
//  E   = curvature (jak rychle pada za peakem; ~0.97)
export function magicFormula(x, B, C, D, E) {
  const Bx = B * x;
  return D * Math.sin(C * Math.atan(Bx - E * (Bx - Math.atan(Bx))));
}

// Podelny slip ratio: (obvodova rychlost kola - rychlost vozu) / |rychlost vozu|
// vFloor zabranuje divergenci u nizke rychlosti (znamy problem Pacejky).
export function slipRatio(omega, radius, vLong, vFloor) {
  return (omega * radius - vLong) / Math.max(Math.abs(vLong), vFloor);
}

// Pricny slip angle (rad). Znamenko orientovano tak, aby sila byla restorujici.
export function slipAngle(vLat, vLong, vFloor) {
  return Math.atan2(-vLat, Math.abs(vLong) + vFloor);
}

// Friction ellipse: zkombinovany podelny+pricny slip nesmi prekrocit grip kruh/elipsu.
// Normalizace per-osa (ruzne mu long/lat), pak zkraceni do elipsy.
// [F-503] Kdyz je max gripu osy ~0 (napr. N=0), je sila te osy 0 (zadny grip = zadna
// sila) - vraci konecnou nulu, ne necleneny vstup. Pro nenulove max je chovani puvodni.
export function frictionEllipse(fLong, fLat, maxLong, maxLat) {
  const fl = maxLong > 1e-6 ? fLong : 0;
  const ft = maxLat > 1e-6 ? fLat : 0;
  const ex = maxLong > 1e-6 ? fl / maxLong : 0;
  const ey = maxLat > 1e-6 ? ft / maxLat : 0;
  const e = Math.hypot(ex, ey);
  if (e > 1) {
    return { fLong: fl / e, fLat: ft / e, saturated: true };
  }
  return { fLong: fl, fLat: ft, saturated: false };
}

// Interpolace engine torque krivky. curve = [[rpm, Nm], ...] vzestupne dle rpm.
export function engineTorque(curve, rpm) {
  // Ochrana proti prazdne/jednobodove krivce (volano kazdy tick - nesmi spadnout sim).
  if (!curve || curve.length === 0) return 0;
  if (curve.length === 1) return curve[0][1];
  if (rpm <= curve[0][0]) return curve[0][1];
  const last = curve[curve.length - 1];
  if (rpm >= last[0]) return last[1];
  for (let i = 1; i < curve.length; i++) {
    if (rpm <= curve[i][0]) {
      const [r0, t0] = curve[i - 1];
      const [r1, t1] = curve[i];
      const f = (rpm - r0) / (r1 - r0);
      return t0 + (t1 - t0) * f;
    }
  }
  return last[1];
}

// Engine RPM z obvodove rychlosti kola (rad/s) pres prevod.
export function wheelOmegaToRpm(omega, gearRatio, finalDrive) {
  return Math.abs(omega) * gearRatio * finalDrive * (60 / (2 * Math.PI));
}

// Jednoduchy ABS regulator: pokud |slip| prekroci prah, snizi brzdny moment.
// Vraci faktor 0..1, kterym se nasobi brzdny moment.
export function absFactor(slip, targetSlip = 0.14, releaseSlip = 0.28) {
  const a = Math.abs(slip);
  if (a <= targetSlip) return 1;
  if (a >= releaseSlip) return 0.15;
  // linearni prechod
  return 1 - 0.85 * ((a - targetSlip) / (releaseSlip - targetSlip));
}

// [F-502] NaN-safe: pri NaN vraci lo (konecna hodnota), aby se NaN slip nesiril
// do magicFormula -> tire force -> addForceAtPoint (jediny NaN by rozbil cely sim).
// Pro vsechny konecne v je chovani nezmenene (puvodni orezani rozsahu).
export function clamp(v, lo, hi) {
  if (v !== v) return lo; // NaN
  return v < lo ? lo : v > hi ? hi : v;
}
